/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useReducer, useEffect } from "react";
import { Else, If, Then } from "react-if";
import { v4 } from "uuid";
import { BrickLoader, FlexContainer } from "@nordcloud/gnui";
import {
  EventActionAttempt,
  EventActionResourceReport,
  EventActionResourceReportsFilterByInput,
  EventActionResourceReportsFilterByFields,
} from "~/generated/graphql";
import { LoaderWrap, NoData } from "~/components";
import { Pagination } from "~/components/Pagination";
import { getNoSearchResultsMessage } from "~/components/SearchInput";
import { useQueryState, useGetSearchPhrase } from "~/hooks";
import { isEmpty } from "~/tools";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { prepareLogErrorCodeFilterBy } from "~/utils/logErrorCodeFilters";
import { ResourceStatus } from "~/views/events/EventsDetails/components/ResourceStatus";
import { useEventDetailsTab } from "~/views/events/EventsDetails/Tabs/Details/EventDetailsTabProvider";
import { useGetEventActionResourceReports } from "~/views/events/hooks/";
import { ReportHeader } from "./ReportHeader";
import { ReportStateCtxProvider, useReportState } from "./ReportProvider";
import {
  FilterType,
  ReportState,
  EventActionResourceReportsQuery,
} from "./types";
import { mapFilterTypeToPackageType } from "./utils";

type Props = {
  eventId: string;
  eventActionId: string;
  attempt?: EventActionAttempt;
};

const prepareFilterBy = (
  reportState: ReportState,
  queryState: EventActionResourceReportsQuery
): EventActionResourceReportsFilterByInput[] | undefined => {
  const packageType = mapFilterTypeToPackageType(reportState.filterQuery);

  const packageTypeFilter =
    createFilterByInput<EventActionResourceReportsFilterByFields>(
      EventActionResourceReportsFilterByFields.PackageType,
      packageType ? [packageType] : [],
      false
    );

  const logOutputFilter = prepareLogErrorCodeFilterBy(
    EventActionResourceReportsFilterByFields.ErrorCode,
    queryState.logOutputs,
    undefined
  );

  const retVal = [...packageTypeFilter, ...logOutputFilter];

  return isEmpty(retVal) ? undefined : retVal;
};

function ReportBox({ eventId, eventActionId, attempt }: Props) {
  const { queryState } = useQueryState<EventActionResourceReportsQuery>();
  const { resourceController } = useEventDetailsTab();
  const { resourceSearchState } = resourceController;
  const { reportState, updateReportState } = useReportState();

  const { phrase, isSearchAllowed } = useGetSearchPhrase(
    resourceSearchState.searchPhrase
  );

  const {
    data: reportResources,
    count,
    loading,
    error,
  } = useGetEventActionResourceReports({
    eventId,
    eventActionId: attempt?.id ? attempt?.id : eventActionId,
    page: queryState.page,
    limit: queryState.limit,
    filter: {
      filterBy: prepareFilterBy(reportState, queryState),
      phrase: phrase ? phrase : undefined,
    },
    includeAttempts: !attempt?.id,
  });

  useEffect(() => {
    if (!loading) {
      updateReportState({ packagesCount: reportResources?.packagesCount });
    }
  }, [reportResources?.packagesCount, loading, updateReportState]);

  return (
    <FlexContainer direction="column" grow={1} alignItems="stretch">
      <ReportHeader eventActionId={eventActionId} />
      <LoaderWrap loading={loading} Component={BrickLoader}>
        <If condition={!isSearchAllowed}>
          <Then>
            <NoData hasIcon message={getNoSearchResultsMessage()} />
          </Then>
          <Else>
            <If condition={!!error}>
              <Then>
                <NoData hasIcon message={error?.message} />
              </Then>
              <Else>
                <If condition={isEmpty(reportResources?.result ?? [])}>
                  <Then>
                    <FlexContainer grow={1} justifyContent="center">
                      <NoData
                        hasIcon
                        message="There is no Report for this event."
                      />
                    </FlexContainer>
                  </Then>
                  <Else>
                    {reportResources?.result?.map(
                      (resource: EventActionResourceReport) => {
                        return (
                          <ResourceStatus
                            key={v4()}
                            eventActionResourceReport={resource}
                          />
                        );
                      }
                    )}
                    <Pagination count={count} />
                  </Else>
                </If>
              </Else>
            </If>
          </Else>
        </If>
      </LoaderWrap>
    </FlexContainer>
  );
}

export function ReportView({ eventId, eventActionId, attempt }: Props) {
  const [reportState, updateReportState] = useReducer(
    (data: ReportState, partialData: Partial<ReportState>) => ({
      ...data,
      ...partialData,
    }),
    {
      resources: undefined,
      filterQuery: FilterType.ALL,
      eventActionId,
    }
  );

  return (
    <ReportStateCtxProvider value={{ reportState, updateReportState }}>
      <ReportBox {...{ eventActionId, eventId, attempt }} />
    </ReportStateCtxProvider>
  );
}
